import { ApiBaseUrl, BearerHeader, JsonHeaders } from '../services/api';

export class ApiError extends Error {
  status: number;
  data?: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Fetches data from an API endpoint.
 *
 * @param {string} url - The URL of the API endpoint to fetch from.
 * @returns {Promise} - A Promise that resolves to the JSON response from the API.
 */
export const apiFetcher = async (url: string) => {
  const res = await fetch(`${ApiBaseUrl}/${url}`);

  let data;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    throw new ApiError(
      data?.message || res.statusText || 'API request failed',
      res.status,
      data
    );
  }

  return data;
};

export const apiFetcherWithoutThrowError = (url: string) =>
  fetch(`${ApiBaseUrl}/${url}`).then((res) => {
    if (res.ok) return res.json();

    return new ApiError(
      res.statusText || 'API request failed',
      res.status,
      null
    );
  });

/**
 * Function that fetches data from an API with authentication.
 *
 * @param {string} url - The URL to fetch the data from.
 * @returns {Promise} A promise that resolves to the fetched data.
 */
export const apiFetcherWithAuth = (url: string) =>
  fetch(`${ApiBaseUrl}/${url}`, { headers: { ...BearerHeader } }).then((res) =>
    res.json()
  );

/**
 * Fetches data from a local URL.
 *
 * @param {string} url - The URL to fetch data from.
 * @returns {Promise} A promise that resolves to the parsed JSON response.
 */
export const localFetcher = (url: string) =>
  fetch(`/${url}`).then((res) => res.json());

/**
 * Fetches data from a remote URL.
 *
 * @param {string} url - The URL to fetch data from.
 * @returns {Promise} A promise that resolves to the parsed JSON response.
 */
export const remoteFetcher = (url: string) =>
  fetch(url, { mode: 'no-cors', headers: { ...JsonHeaders } }).then((res) =>
    res.json()
  );

export const apiMutation = async <TArg, TResponse = unknown>(
  url: string,
  {
    arg
  }: {
    arg: TArg;
  }
): Promise<TResponse> => {
  const res = await fetch(`${ApiBaseUrl}/${url}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(arg)
  });

  return res.json();
};

export const apiMutationStream = async <TArg>(
  url: string,
  arg: TArg,
  signal: AbortSignal,
  onMessage: (payload: { event: string; data: string }) => void,
  onComplete?: (videoUrl: string) => void,
  onAbort?: () => void
) => {
  const res = await fetch(`${url}`, {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(arg),
    signal
  });

  if (!res.ok || !res.body) throw new Error('Failed to connect to stream');

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');

  let buffer = '';
  let isErrorStream = false;

  try {
    let videoUrl = '';
    while (true) {
      if (signal.aborted) {
        reader.cancel();
        onAbort?.();
        return;
      }

      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const messages = buffer.split('\n\n');
      buffer = messages.pop() || '';

      for (const message of messages) {
        const lines = message.split('\n');

        let event = 'message';
        let data = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            event = line.replace('event:', '').trim();
            if (event.includes('error')) isErrorStream = true;
          }

          if (line.startsWith('data:')) {
            data += line.replace('data:', '').trim();
            if (event.includes('complete')) {
              const result = JSON.parse(data) as { url: string };
              videoUrl = result.url;
            }
          }
        }

        onMessage({ event, data });
      }
    }

    if (!isErrorStream) onComplete?.(videoUrl);
  } catch (err: any) {
    if (err.name === 'AbortError') {
      onAbort?.();
      return;
    }
    throw err;
  } finally {
    reader.releaseLock();
  }
};
